<template>
  <header class="main-header">
    <div class="header-container">
      <!-- 左侧：多标签栏 -->
      <div class="tabs-row">
        <el-tabs v-model="tabsStore.active" type="card" :closable="false">
          <el-tab-pane v-for="t in tabsStore.tabs" :key="t.name" :name="t.name">
            <template #label>
              <div class="edge-tab">
                <el-tooltip
                  effect="dark"
                  :content="t.title"
                  placement="top"
                  :disabled="!isTextOverflow(t.title)"
                >
                  <span
                    class="edge-tab-title"
                    :class="{ 'is-left-align': t.title && t.title.includes('-') }"
                  >
                    {{ t.title }}
                  </span>
                </el-tooltip>
                <span class="edge-tab-close" @click.stop="handleTabRemove(t.name)">✕</span>
              </div>
            </template>
          </el-tab-pane>
        </el-tabs>
      </div>

      <!-- 右侧：用户操作区 -->
      <div class="user-actions">
        <div class="feedback-button">
          <el-dropdown trigger="hover" placement="bottom" :show-timeout="0">
            <img :src="currentIcon" alt="问题反馈" class="feedback-icon" />
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item @click="openFeedback">问题反馈</el-dropdown-item>
                <el-dropdown-item @click="openFeedbackList">问题列表</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>

        <div class="vertical-divider"></div>

        <nav class="nav-links">
          <img class="logo" src="../assets/个人中心.png" alt="Logo" />
          <el-dropdown>
            <span class="el-dropdown-link user-link">
              {{ username }}
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item @click="handleLogout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </nav>
      </div>
    </div>
  </header>
  <FeedbackDialog
    ref="feedbackDialogRef"
    @close="resetFeedbackIcon"
    :current-page="currentPageName"
  />
  <FeedbackDialogList ref="feedbackDialogListRef" @close="resetFeedbackIcon" />
</template>

<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router'
import { useTabsStore } from '@/stores/tabs'
import type { TabPaneName } from 'element-plus'
import { ref, onMounted, computed, nextTick } from 'vue'
import { getCookie, clearAuthCookies, parseJwt } from '@/utils/auth'
import request from '@/utils/request'
import image1 from '@/assets/问题反馈.svg'
import image2 from '@/assets/点击问题反馈.svg'
import FeedbackDialog from '@/components/FeedbackDialog.vue'
import FeedbackDialogList from '@/components/FeedbackDialogList.vue'
import Sortable from 'sortablejs'
const feedbackDialogRef = ref()
const feedbackDialogListRef = ref()
const openFeedback = () => {
  feedbackDialogRef.value?.open()
  toggleImag.value = true
  currentIcon.value = image2
}

const openFeedbackList = () => {
  feedbackDialogListRef.value?.open()
  toggleImag.value = true
  currentIcon.value = image2
}

// 重置图标到初始状态的方法
const resetFeedbackIcon = () => {
  toggleImag.value = false
  currentIcon.value = image1
}

//问题反馈图标
const currentIcon = ref(image1)
const toggleImag = ref(false)

const router = useRouter()
const route = useRoute()
const tabsStore = useTabsStore()
const username = ref('Guest')

// 页面配置：定义每个页面的名称映射
interface PageConfig {
  name: string
  category: 'monitor' | 'disposal' | 'user' | 'media' | 'accountability'
}

const pageConfigs: Record<string, PageConfig> = {
  PublicopinionDashboard: { name: '舆情看板', category: 'monitor' },
  PublicopinionDetail: { name: '舆情详情', category: 'monitor' },
  PublicopinionContent: { name: '舆情内容', category: 'monitor' },
  AccountPostList: { name: '账号帖子', category: 'monitor' },
  ArchiveDashboard: { name: '归档看板', category: 'disposal' },
  ArchiveDetail: { name: '归档详情', category: 'disposal' },
  ProcessMonitor: { name: '流程监听', category: 'disposal' },
  InternalDetails: { name: '内部详情', category: 'disposal' },
  InternalDetailsNext: { name: '内部详情内容', category: 'disposal' },
  ZmnDetails: { name: 'Zmn工单详情', category: 'disposal' },
  YeyxDetails: { name: '言而有信工单详情', category: 'disposal' },
  ComplaintDetails: { name: '投诉详情', category: 'disposal' },
  PersonalInformation: { name: '个人信息', category: 'user' },
  AuthorityManagement: { name: '权限管理', category: 'user' },
  CompetentAuthority: { name: '主管部门', category: 'media' },
  ServiceProvider: { name: '合作服务商', category: 'media' },
  MediaLibrary: { name: '媒体资源库', category: 'media' },
  MediaReport: { name: '媒体报道', category: 'media' },
  MediaResourceDashboard: { name: '媒体资源看板', category: 'media' },
  MediaDetails: { name: '媒体详情', category: 'media' },
  TaskTrack: { name: '任务追踪', category: 'accountability' },
  AccountabilityDashboard: { name: '追责看板', category: 'accountability' },
  AccountabilityList: { name: '追责列表', category: 'accountability' },
  Rectification: { name: '整改任务', category: 'accountability' },
}

// 获取当前页面名称
const currentPageName = computed(() => {
  const routeName = route.name as string
  const config = pageConfigs[routeName]
  return config ? config.name : ''
})

// 判断文字是否超出宽度（基于字符数量简单判断）
function isTextOverflow(text: string): boolean {
  return text.length > 5
}
function handleTabRemove(name: TabPaneName) {
  const next = tabsStore.remove(String(name))
  if (next) {
    router.push(next)
  } else {
    // 标签栏为空时刷新页面
    window.location.reload()
  }
}

async function handleLogout() {
  try {
    // 调用后端登出API
    await request({
      url: '/auth/logout',
      method: 'POST',
    })

    // 清除认证cookie
    clearAuthCookies()
    // 重置用户信息，避免下次进入时残留旧数据
    try {
      const { useUserStore } = await import('@/stores/user')
      const s = useUserStore()
      s.reset()
    } catch (e) {
      console.warn('重置用户信息失败（可忽略）:', e)
    }

    setTimeout(() => {
      window.location.href = '/login'
    }, 1500)
  } catch (error) {
    console.error('Logout failed:', error)
    // 即使登出API失败，也清除本地cookie
    clearAuthCookies()
    try {
      const { useUserStore } = await import('@/stores/user')
      const s = useUserStore()
      s.reset()
    } catch (e) {
      console.warn('重置用户信息失败（可忽略）:', e)
    }
    window.location.href = '/login'
  }
}

// 初始化标签拖拽排序功能
const initTabsDraggable = () => {
  nextTick(() => {
    const tabsNav = document.querySelector('.tabs-row .el-tabs__nav') as HTMLElement
    if (!tabsNav) return

    Sortable.create(tabsNav, {
      animation: 200, // 动画时长
      ghostClass: 'sortable-ghost', // 拖拽时的幽灵元素样式
      dragClass: 'sortable-drag', // 被拖拽元素的样式
      chosenClass: 'sortable-chosen', // 选中元素的样式
      forceFallback: false, // 使用原生拖拽
      fallbackClass: 'sortable-fallback',
      fallbackOnBody: true,
      swapThreshold: 0.65, // 交换阈值，让拖拽更流畅
      direction: 'horizontal', // 水平拖拽

      // 拖拽结束时更新标签顺序
      onEnd: (evt) => {
        const { oldIndex, newIndex } = evt
        if (oldIndex === undefined || newIndex === undefined || oldIndex === newIndex) return

        const newTabs = [...tabsStore.tabs]
        const movedTab = newTabs.splice(oldIndex, 1)[0]
        newTabs.splice(newIndex, 0, movedTab)

        // 更新 store 中的标签顺序
        tabsStore.reorderTabs(newTabs)
      },
    })
  })
}

onMounted(() => {
  // username.value = getCookie('username') || 'Guest'
  //从 token 中解析用户名以及id
  const token = getCookie('token')

  if (token) {
    const payload = parseJwt(token)
    username.value = payload?.username || 'Guest'
    console.log('获取到的数据：', payload.username, payload.userid)
  } else {
    console.warn('Token 不存在或已过期')
  }

  // 初始化拖拽功能
  initTabsDraggable()
})
</script>

<style scoped>
.main-header {
  width: 100%;
  height: 100%;
  background: #fff; /* 恢复纯白，更显高级 */
  box-shadow: 0 1px 0 0 #f0f0f0; /* 使用极细的底边线代替投影 */
  z-index: 1000;
  user-select: none;
}

.header-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  padding: 0;
  gap: 0;
}

/* 标签栏区域 */
.tabs-row {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center; /* 居中对齐，不再底部对齐 */
  height: 100%;
  padding: 0 12px;
  background: #fff;
}

.tabs-row :deep(.el-tabs) {
  width: 100%;
  height: 38px; /* 统一样式高度 */
}

.tabs-row :deep(.el-tabs__header) {
  margin: 0;
  border-bottom: none !important;
}

.tabs-row :deep(.el-tabs__nav-wrap) {
  margin-bottom: 0;
}

.tabs-row :deep(.el-tabs__nav-wrap:after) {
  display: none !important;
}

.tabs-row :deep(.el-tabs__nav) {
  border: none !important;
}

/* 优化后的标签项样式：胶囊风格 */
.tabs-row :deep(.el-tabs__item) {
  height: 32px !important;
  line-height: 32px !important;
  margin: 8px 4px !important;
  padding: 0 12px !important;
  border: none !important;
  border-radius: 6px;
  background: transparent;
  color: #595959;
  font-weight: 400;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 100px;
  max-width: 180px;
}

.tabs-row :deep(.el-tabs__item.is-active) {
  background: rgba(63, 131, 244, 0.08) !important;
  color: #3f83f4 !important;
  font-weight: 600;
  z-index: 1;
}

.tabs-row :deep(.el-tabs__item:hover:not(.is-active)) {
  background: #f5f5f5;
  color: #262626;
}

/* Edge 风格标签内容 */
.edge-tab {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 100%;
}

.edge-tab-title {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  letter-spacing: 0.3px;
}

.edge-tab-close {
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: #bfbfbf;
  border-radius: 50%;
  transition: all 0.2s;
  flex-shrink: 0;
}

.edge-tab-close:hover {
  background-color: rgba(0, 0, 0, 0.08);
  color: #ff4d4f;
}

.is-active .edge-tab-close:hover {
  background-color: #ffeded;
  color: #f56c6c;
}

/* 右侧：用户操作区 */
.user-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  height: 100%;
  padding: 0 24px;
  background: #fff;
}

.vertical-divider {
  width: 3px;
  height: 18px;
  background-color: #f0f0f0;
  margin: 0 4px;
}

.feedback-button {
  display: flex;
  align-items: center;
}

.feedback-icon {
  cursor: pointer;
  width: 26px; /* 增加反馈图标大小 */
  height: 26px;
  transition: transform 0.2s;
  outline: none;
}

.feedback-icon:hover {
  transform: scale(1.1);
}

.nav-links {
  display: flex;
  align-items: center;
}

.logo {
  width: 22px; /* 增加头像大小 */
  height: 22px;
  border-radius: 50%;
  margin-right: 10px;
}

.user-link {
  font-size: 16px;
  color: #303133;
  cursor: pointer;
  transition: color 0.2s;
  font-weight: normal; /* 去掉加粗 */
  outline: none !important; /* 去掉聚焦时的轮廓线 */
}

.user-link:focus,
.user-link:focus-visible,
.user-link:active {
  outline: none !important; /* 深度清除各种状态下的边框/框线 */
}

/* 针对 Element Plus 下拉触发元素的特殊处理，彻底去除蓝色或黑色焦点框 */
:deep(.el-dropdown:focus-visible),
:deep(.el-dropdown-link:focus-visible) {
  outline: none !important;
}

.user-link:hover {
  color: #409eff;
}

/* ========== 拖拽排序样式 ========== */
.tabs-row :deep(.sortable-ghost) {
  opacity: 0.3;
  background: #3f83f4 !important;
  border-radius: 6px;
}

.tabs-row :deep(.sortable-drag) {
  background: #fff;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  transform: scale(1.05);
}
</style>
